import { Typography } from '@/components/ui/typography'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useFormContext } from '@/lib/form-contexts'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import {
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  TiktokIcon,
  YoutubeIcon,
} from '@/components/form/SocialIcons'
import { Clock, Image, MapPin, PersonStanding, Phone, Store, Truck } from 'lucide-react'

const PREFIX = 'pages./.blocks.footer'
const name = (str) => `${PREFIX}.${str}`
const visitOurShop = (str) => name(`visitOurShop.0.${str}`)
const footer = (str) => name(`footer.0.${str}`)

const FooterSectionForm = () => {
  const form = useFormContext()

  return (
    <>
      <div className="mb-8 flex flex-col gap-y-4">
        <div>
          <Typography variant="h4" className="flex items-center gap-2">
            <Store className="size-5" /> Visit Our Shop
          </Typography>
          <Typography variant="supporting">
            Choose what customers see in your storefront's store details section.
          </Typography>
        </div>
        <div className="ml-6 mt-4">
          <div className="grid grid-cols-1 gap-5">
            <div>
              <form.AppField name={visitOurShop('media.0')}>
                {(field) => (
                  <field.MediaUploadInput
                    className="max-w-xl"
                    allowFocalPoint
                    uploadUsage={MediaUploadUsage.StorefrontHero.Value}
                    label={
                      <Typography variant="emphasis" className="flex items-center gap-2">
                        <Image className="size-5" /> Store Image
                      </Typography>
                    }
                    description="So customers can recognize your store when they visit."
                  />
                )}
              </form.AppField>
            </div>

            <div>
              <div className="flex flex-col gap-y-2 w-full mt-1">
                <Typography variant="emphasis" className="flex items-center gap-2">
                  <MapPin />
                  Store Details to Show
                </Typography>
                <Typography variant="supporting">
                  Turn each item on or off for your storefront.
                </Typography>
              </div>
              <div className="flex flex-col gap-y-4 mt-2">
                <form.AppField name={visitOurShop('props.showHours')}>
                  {(field) => (
                    <field.SwitchInput
                      label={
                        <div className="inline-flex items-center gap-2">
                          <Clock className="size-5" />
                          Store Hours
                        </div>
                      }
                    />
                  )}
                </form.AppField>
                <form.AppField name={visitOurShop('props.showPhone')}>
                  {(field) => (
                    <field.SwitchInput
                      label={
                        <div className="inline-flex items-center gap-2">
                          <Phone className="size-5" />
                          Phone Number
                        </div>
                      }
                    />
                  )}
                </form.AppField>
                <form.AppField name={visitOurShop('props.showAddress')}>
                  {(field) => (
                    <field.SwitchInput
                      label={
                        <div className="inline-flex items-center gap-2">
                          <MapPin className="size-5" />
                          Store Address
                        </div>
                      }
                    />
                  )}
                </form.AppField>
                <form.AppField name={visitOurShop('props.showDeliveryZipCodes')}>
                  {(field) => (
                    <field.SwitchInput
                      label={
                        <div className="inline-flex items-center gap-2">
                          <Truck className="size-5" />
                          Show Delivery Zip Codes
                        </div>
                      }
                    />
                  )}
                </form.AppField>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 flex flex-col gap-y-4">
        <div>
          <Typography variant="h3" className="flex items-center gap-2">
            <Image className="size-6" /> Footer Logo
          </Typography>
          <Typography variant="supporting">
            Choose the logo and size shown against your footer color.
          </Typography>
        </div>
        <div className="ml-6 grid max-w-xl grid-cols-1 gap-5">
          <form.AppField name="brand.media.footer_logo">
            {(field) => (
              <field.MediaUploadInput
                photoManagerMode={PhotoManagerMode.UploadOnlySaved}
                label="Footer Logo"
                description="Used only in the storefront footer."
              />
            )}
          </form.AppField>
          <form.AppField name={footer('props.logoMaxHeightPx')}>
            {(field) => (
              <field.SliderInput
                label="Footer Logo Height"
                thumbAriaLabel="Footer Logo Height"
                step={1}
                min={16}
                max={400}
                unit="px"
              />
            )}
          </form.AppField>
        </div>
      </div>
      <div className="mb-4">
        <Typography variant="h3" className="flex items-center gap-2">
          <PersonStanding className="size-6" /> Social Media
        </Typography>
        <Typography variant="supporting">
          Add your social profiles. If you enter only a handle, we will format it into a full URL.
        </Typography>
      </div>
      <div className="flex flex-col gap-4">
        {/* pages -> homepage -> blocks -> footer region -> footer blocks -> first block -> props -> field  */}
        <form.AppField name={footer('props.youtube_link')}>
          {(field) => (
            <field.SocialUrlInput
              label={
                <div className="inline-flex items-center gap-2">
                  <YoutubeIcon className="size-5" />
                  YouTube
                </div>
              }
              placeholder="https://www.youtube.com/channel/123456789"
            />
          )}
        </form.AppField>
        <form.AppField name={footer('props.instagram_link')}>
          {(field) => (
            <field.SocialUrlInput
              label={
                <div className="inline-flex items-center gap-2">
                  <InstagramIcon className="size-5" />
                  Instagram
                </div>
              }
              placeholder="https://www.instagram.com/your-username"
            />
          )}
        </form.AppField>
        <form.AppField name={footer('props.facebook_link')}>
          {(field) => (
            <field.SocialUrlInput
              label={
                <div className="inline-flex items-center gap-2">
                  <FacebookIcon className="size-5" />
                  Facebook
                </div>
              }
              placeholder="https://www.facebook.com/your-username"
            />
          )}
        </form.AppField>
        <form.AppField name={footer('props.pinterest_link')}>
          {(field) => (
            <field.SocialUrlInput
              label={
                <div className="inline-flex items-center gap-2">
                  <PinterestIcon className="size-5" />
                  Pinterest
                </div>
              }
              placeholder="https://www.pinterest.com/your-username"
            />
          )}
        </form.AppField>
        <form.AppField name={footer('props.tiktok_link')}>
          {(field) => (
            <field.SocialUrlInput
              label={
                <div className="inline-flex items-center gap-2">
                  <TiktokIcon className="size-5" />
                  TikTok
                </div>
              }
              placeholder="https://www.tiktok.com/@your-username"
            />
          )}
        </form.AppField>
      </div>
    </>
  )
}

export default FooterSectionForm
